import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageSeo from "@/components/PageSeo";
import { SITE } from "@/constants/testIds";

const PRINCIPLES = [
  { n: "01", t: "Problems first.", d: "We don't start with services or shotlists. We start with what isn't working — and whether film is even the right answer." },
  { n: "02", t: "Context over craft.", d: "Craft is table stakes. The reason a film lands or fails is almost always context the brief didn't surface." },
  { n: "03", t: "Specialists, not generalists.", d: "We assemble crews per project. Director-DP combinations chosen for the brief, not the rolodex." },
  { n: "04", t: "Honest about scope.", d: "If the brief is wrong, we say so before the shoot. If the budget is wrong, we say so before the proposal." },
  { n: "05", t: "Made to be remembered.", d: "Reach is a metric. Memory is the goal. We optimise for the second viewing." },
];

const ABOUT_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "mainEntity": {
    "@type": "Person",
    "@id": "https://relatable.studio/about#rudhra-vishnu",
    "name": "Rudhra Vishnu",
    "givenName": "Rudhra",
    "familyName": "Vishnu",
    "jobTitle": "Founder & Creative Director",
    "founder": { "@id": "https://relatable.studio/#organization" },
    "worksFor": {
      "@type": "Organization",
      "@id": "https://relatable.studio/#organization",
      "name": "Relatable Studio",
      "url": "https://relatable.studio",
      "foundingDate": "2020",
      "founder": { "@id": "https://relatable.studio/about#rudhra-vishnu" }
    },
    "url": "https://relatable.studio/about",
    "nationality": { "@type": "Country", "name": "India" },
    "workLocation": {
      "@type": "Place",
      "name": "BHIVE Platinum, Church Street Campus, Bengaluru",
      "address": { "@type": "PostalAddress", "addressLocality": "Bengaluru", "addressRegion": "Karnataka", "addressCountry": "IN" }
    },
    "description": "Founder of Relatable Studio (formerly Straight Arrow Studios). Bangalore-based filmmaker and production lead. 1600+ projects delivered across India for 300+ brands.",
    "knowsAbout": [
      "Brand films",
      "Documentary filmmaking",
      "Cinematography",
      "Creative direction",
      "Content strategy",
      "Founder storytelling",
      "Employer branding films",
      "Campaign production"
    ],
    "hasOccupation": {
      "@type": "Occupation",
      "name": "Film Director & Studio Founder",
      "occupationLocation": { "@type": "City", "name": "Bengaluru" }
    }
  }
};

export default function About() {
  return (
    <main data-testid={SITE.aboutPage} className="bg-ink text-bone min-h-screen">
      <PageSeo
        title="About Relatable Studio · Founder Rudhra Vishnu · Bangalore Production House"
        description="A storytelling company disguised as a production house. Founded by Rudhra Vishnu in 2020 as Straight Arrow Studios. Rebranded to Relatable Studio in 2026. 1600+ projects across India for 300+ brands."
        path="/about"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ]}
        jsonLd={ABOUT_JSON_LD}
      />
      <section className="pt-28 md:pt-32 pb-16 md:pb-20 px-6 md:px-12 lg:px-24">
        <p className="eyebrow text-fog mb-6">◐ About — Studio Philosophy</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-9xl tracking-tighter font-medium leading-[0.92] max-w-[20ch]">
          A storytelling<br/>company<br/>disguised as a<br/><span className="text-relatable">production house.</span>
        </h1>
      </section>

      <section className="py-16 md:py-20 px-6 md:px-12 lg:px-24 border-t border-line">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow text-fog">◇ The Long Version</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-5 leading-[1]">
              One founder.<br/>One camera.<br/>One studio.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 text-fog text-lg leading-relaxed space-y-6">
            <p>
              In 2020, <span className="text-bone">Rudhra Vishnu</span> started Straight Arrow Studios. A camera, a small Bangalore studio with a projection wall, and a stubborn rule: make work that earns its runtime. No fixed roster. No agency mark-up. Every project assembled around the brief, not the other way around.
            </p>
            <p>
              Six years later, we&rsquo;ve delivered 1600+ projects with 300+ brands and 700+ creative partners. The same lesson kept showing up: the projects that mattered most weren&rsquo;t the ones with the biggest budgets or the prettiest grades. They were the ones where we slowed down enough to <span className="text-bone">ask what was actually broken</span> — and only then picked up the camera.
            </p>
            <p>
              That&rsquo;s the business we wanted to be in. In 2026 we gave it a name we couldn&rsquo;t hide behind. <span className="text-bone font-display">RELATABLE</span> isn&rsquo;t a rebrand of a production company. It&rsquo;s the studio Straight Arrow was always trying to become.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 px-6 md:px-12 lg:px-24 border-t border-line bg-bone text-ink">
        <p className="eyebrow text-ink/60 mb-6">◇ Five Principles</p>
        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter font-medium leading-[0.95] max-w-3xl">
          What we&rsquo;ll always do.<br/>What we won&rsquo;t.
        </h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/15">
          {PRINCIPLES.map((p, i) => (
            <motion.div
              key={p.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.5 }}
              className="bg-bone p-10 md:p-14"
            >
              <span className="font-mono text-xs text-ink/50">{p.n}</span>
              <h3 className="mt-4 font-display text-3xl md:text-4xl tracking-tight font-medium">{p.t}</h3>
              <p className="mt-5 text-ink/70 text-base md:text-lg leading-relaxed">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-24 px-6 md:px-12 lg:px-24 border-t border-line">
        <p className="eyebrow text-fog mb-6">◇ How We Work</p>
        <div className="grid grid-cols-12 gap-8">
          <h2 className="col-span-12 md:col-span-5 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium leading-[1]">
            A network,<br/>not a roster.
          </h2>
          <div className="col-span-12 md:col-span-7 text-fog text-lg leading-relaxed space-y-6">
            <p>
              We don&rsquo;t carry a fixed creative team. For each project we build a crew from a vetted network of directors, DPs, editors, motion artists, sound designers, line producers and experience designers across India.
            </p>
            <p>
              The benefit isn&rsquo;t cheaper. It&rsquo;s sharper. The core team in Bangalore handles strategy, story and operations. Everything else is matched to the brief.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24 border-t border-line text-center">
        <h2 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95]">
          Ready when you are.
        </h2>
        <Link to="/contact" className="mt-10 inline-flex items-center gap-3 px-7 py-4 bg-bone text-ink eyebrow hover:bg-relatable hover:text-bone transition-colors">
          Start The Conversation →
        </Link>
      </section>
    </main>
  );
}
